import { useMutation } from "react-query";
import {
  bulkOnboardCompanies,
  onboardFromParsed,
  parseCaptableExcel,
  parseCaptableAI,
  getCaptableAIBatchStatus,
  removeCaptableSourceFile,
  uploadCompanyExcel,
} from "../../api/companyExcelOnboarding";
import {
  editCaptable,
  onboardStage3Company,
} from "../../api/companyStage3Onboarding";
import { handleEventForTracking } from "../../amplitudeAnalytics";
import { queryClient } from "../client";

export function useUploadCompanyExcel() {
  return useMutation({
    mutationKey: "uploadCompanyExcel",
    mutationFn: uploadCompanyExcel,
    onSuccess: () => {
      handleEventForTracking({
        eventName: "uploadCompanyExcel",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "uploadCompanyExcel",
        success: false,
        eventType: "API",
      });
    },
  });
}

export function useParseCaptableExcel() {
  return useMutation({
    mutationKey: "parseCaptableExcel",
    mutationFn: parseCaptableExcel,
    onSuccess: () => {
      handleEventForTracking({
        eventName: "parseCaptableExcel",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "parseCaptableExcel",
        success: false,
        eventType: "API",
      });
    },
  });
}

export function useParseCaptableAI() {
  return useMutation({
    mutationKey: "parseCaptableAI",
    mutationFn: parseCaptableAI,
  });
}

export function useGetCaptableAIBatchStatus() {
  return useMutation({
    mutationKey: "getCaptableAIBatchStatus",
    mutationFn: getCaptableAIBatchStatus,
  });
}

export function useBulkOnboardCompanies() {
  return useMutation({
    mutationKey: "bulkOnboardCompanies",
    mutationFn: bulkOnboardCompanies,
    onSuccess: async () => {
      // The upload can create multiple companies and assign account users to
      // them. Mark the list stale so active screens refresh once through
      // React Query, without forcing a refetch from inside the mutation.
      await Promise.all([
        queryClient.invalidateQueries("userCompanies"),
        queryClient.invalidateQueries("accountUsers"),
      ]);
      handleEventForTracking({
        eventName: "bulkOnboardCompanies",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "bulkOnboardCompanies",
        success: false,
        eventType: "API",
      });
    },
  });
}

export function useOnboardFromParsed() {
  return useMutation({
    mutationKey: "onboardFromParsed",
    mutationFn: onboardFromParsed,
    onSuccess: () => {
      queryClient.refetchQueries("userCompanies");
      handleEventForTracking({
        eventName: "onboardFromParsed",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "onboardFromParsed",
        success: false,
        eventType: "API",
      });
    },
  });
}

export function useEditCaptable() {
  return useMutation({
    mutationKey: "editCaptable",
    mutationFn: editCaptable,
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries("userCompanies"),
        queryClient.invalidateQueries("captable"),
        queryClient.invalidateQueries("company"),
      ]);
      handleEventForTracking({
        eventName: "editCaptable",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "editCaptable",
        success: false,
        eventType: "API",
      });
    },
  });
}

export function useRemoveCaptableSourceFile() {
  return useMutation({
    mutationKey: "removeCaptableSourceFile",
    mutationFn: removeCaptableSourceFile,
    onSuccess: () => {
      handleEventForTracking({
        eventName: "removeCaptableSourceFile",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "removeCaptableSourceFile",
        success: false,
        eventType: "API",
      });
    },
  });
}

export function useOnboardStage3Company() {
  return useMutation({
    mutationKey: "onboardStage3Company",
    mutationFn: onboardStage3Company,
    onSuccess: () => {
      queryClient.refetchQueries("userCompanies");
      handleEventForTracking({
        eventName: "onboardStage3Company",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "onboardStage3Company",
        success: false,
        eventType: "API",
      });
    },
  });
}
